import React, { useState } from "react";
import { createRoot, Root } from "react-dom/client";
import { ErrorModal } from "./ErrorModal";

const RETRY_MESSAGE =
  "There was an error sending prescription to Titan. Please try again.";
const CONTACT_SUPPORT_MESSAGE =
  "There was an error sending prescription to Titan. Please contact support.";

interface ShowPrescriptionSendErrorOptions {
  isDarkMode?: boolean;
  // Resolves success: true when the retry succeeded; the popup closes.
  onRetry: () => Promise<{ success: boolean; message?: string }>;
}

let disposeActive: (() => void) | null = null;

const PrescriptionSendErrorPopup = ({
  isDarkMode,
  onRetry,
  onDispose,
}: ShowPrescriptionSendErrorOptions & { onDispose: () => void }) => {
  const [open, setOpen] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  // Only one retry is offered; after it fails the user is sent to support.
  const [retryFailed, setRetryFailed] = useState(false);

  const close = () => {
    setOpen(false);
    // Let the dialog close animation finish before tearing down the root.
    setTimeout(onDispose, 200);
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      const result = await onRetry();
      if (result?.success) {
        close();
        return;
      }
      setRetryFailed(true);
    } catch (error) {
      console.error("Prescription retry failed:", error);
      setRetryFailed(true);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <ErrorModal
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title="Prescription not sent"
      message={retryFailed ? CONTACT_SUPPORT_MESSAGE : RETRY_MESSAGE}
      isDarkMode={isDarkMode}
      confirmText="Close"
      onRetry={retryFailed ? undefined : handleRetry}
      isRetrying={isRetrying}
    />
  );
};

// Rendered in its own root so it survives the prescription modal closing.
export const showPrescriptionSendError = (
  options: ShowPrescriptionSendErrorOptions,
) => {
  disposeActive?.();

  const container = document.createElement("div");
  document.body.appendChild(container);
  const root: Root = createRoot(container);

  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    root.unmount();
    container.remove();
    if (disposeActive === dispose) disposeActive = null;
  };
  disposeActive = dispose;

  root.render(<PrescriptionSendErrorPopup {...options} onDispose={dispose} />);
};
